import React from "react";
import {
  Card,
  CardBody,
  CardTitle,
  Row,
  Col,
  Progress
} from "reactstrap";
import { funnelHasBackendData } from "utils/adminDashboardFunnel";

function DashboardStatSkeleton({ label }) {
  return (
    <span className="d-inline-flex flex-column" aria-busy="true">
      <span className="sr-only">Loading {label}</span>
      <span className="dashboard-stat-skeleton__value" aria-hidden="true" />
    </span>
  );
}

function fmtNum(v) {
  if (v == null || Number.isNaN(Number(v))) return "—";
  return Number(v).toLocaleString();
}

function fmtDays(v) {
  if (v == null || Number.isNaN(Number(v))) return "—";
  return `${Number(v).toFixed(1)} d`;
}

/**
 * Tier A dashboard: pipeline, timings, stale assigned, Laravel funnel fields,
 * content-type mix. Per-marker table: AdminMarkerWorkloadSection.
 * Funnel counts fall back to headline stats when the aggregate omits them.
 */
export default function AdminTierAMetrics({
  funnel,
  funnelLoading,
  admin_available,
  assigned_total,
  admin_marked,
  statsLoading
}) {
  if (statsLoading.assessed || statsLoading.available) return null;

  const av =
    funnel && funnel.available != null ? funnel.available : admin_available;
  const as =
    funnel && funnel.assigned != null ? funnel.assigned : assigned_total;
  const mk = funnel && funnel.marked != null ? funnel.marked : admin_marked;
  const maxBar = Math.max(
    Number(av) || 0,
    Number(as) || 0,
    Number(mk) || 0,
    Number(funnel && funnel.marks_pending) || 0,
    1
  );

  const backend = funnelHasBackendData(funnel);
  const pct = (a, b) =>
    b > 0 ? `${Math.min(100, Math.round((100 * a) / b))}%` : "—";

  /** Laravel assigned = unmarked with marker; completion rate uses marked + that WIP. */
  const funnelAssignedIsWipOnly =
    funnel && funnel.assigned != null && funnel.marked != null;
  const markedVsAssignedLabel = funnelAssignedIsWipOnly
    ? "Marked / (marked + assigned WIP)"
    : "Marked / assigned";
  const markedVsAssignedPct = funnelAssignedIsWipOnly
    ? pct(Number(mk) || 0, (Number(mk) || 0) + (Number(as) || 0))
    : pct(Number(mk) || 0, Number(as) || 0);

  const markerRows = (funnel && funnel.by_marker) || [];
  const ctRows = (funnel && funnel.by_content_type) || [];

  return (
    <Row className="mt-2">
      <Col xs="12">
        <Card className="card-stats mb-4 mb-xl-0 shadow-sm">
          <CardBody>
            <CardTitle tag="h5" className="text-uppercase text-muted mb-2">
              Pipeline and operations
              {funnel && funnel.year != null ? (
                <span className="text-lowercase font-weight-normal small text-muted ml-2">
                  ({funnel.year})
                </span>
              ) : null}
            </CardTitle>
            {!backend && (
              <p className="small text-muted mb-3">
                Extended metrics load from{" "}
                <code className="text-dark">
                  GET /api/marker-portal-dashboard-funnel?year=
                </code>
                . Until then, the pipeline bar uses the same counts as the cards
                above.
              </p>
            )}

            {funnelLoading && !funnel ? (
              <div className="py-3">
                <DashboardStatSkeleton label="pipeline metrics" />
              </div>
            ) : (
              <>
                <Row className="mb-3">
                  <Col md="4" sm="6" className="mb-3">
                    <small className="text-muted d-block">Available</small>
                    <span className="h4 mb-0">{fmtNum(av)}</span>
                    <Progress
                      max={100}
                      value={(100 * (Number(av) || 0)) / maxBar}
                      className="mt-2"
                    />
                  </Col>
                  <Col md="4" sm="6" className="mb-3">
                    <small className="text-muted d-block">Assigned</small>
                    <span className="h4 mb-0">{fmtNum(as)}</span>
                    <Progress
                      max={100}
                      color="warning"
                      value={(100 * (Number(as) || 0)) / maxBar}
                      className="mt-2"
                    />
                  </Col>
                  <Col md="4" sm="6" className="mb-3">
                    <small className="text-muted d-block">Marked (assessed)</small>
                    <span className="h4 mb-0">{fmtNum(mk)}</span>
                    <Progress
                      max={100}
                      color="success"
                      value={(100 * (Number(mk) || 0)) / maxBar}
                      className="mt-2"
                    />
                  </Col>
                </Row>

                <Row className="mb-2">
                  <Col md="4" sm="6" className="mb-2">
                    <small className="text-muted d-block">
                      {markedVsAssignedLabel}
                    </small>
                    <strong>{markedVsAssignedPct}</strong>
                  </Col>
                  <Col md="4" sm="6" className="mb-2">
                    <small className="text-muted d-block">
                      Median days to mark
                    </small>
                    <strong>{fmtDays(funnel && funnel.median_days_to_mark)}</strong>
                  </Col>
                  <Col md="4" sm="6" className="mb-2">
                    <small className="text-muted d-block">P90 days to mark</small>
                    <strong>{fmtDays(funnel && funnel.p90_days_to_mark)}</strong>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md="4" sm="6" className="mb-2">
                    <small className="text-muted d-block">
                      Stale assigned (not marked)
                    </small>
                    <strong>
                      {funnel && funnel.stale_assigned_count != null
                        ? fmtNum(funnel.stale_assigned_count)
                        : "—"}
                    </strong>
                    {funnel && funnel.stale_assigned_days_threshold != null ? (
                      <small className="d-block text-muted">
                        &gt; {funnel.stale_assigned_days_threshold} days
                      </small>
                    ) : null}
                  </Col>
                  <Col md="4" sm="6" className="mb-2">
                    <small className="text-muted d-block">Marks uploaded</small>
                    <strong>
                      {funnel && funnel.marks_uploaded != null
                        ? fmtNum(funnel.marks_uploaded)
                        : "—"}
                    </strong>
                  </Col>
                  <Col md="4" sm="6" className="mb-2">
                    <small className="text-muted d-block">
                      Pending (unmarked, total)
                    </small>
                    <strong>
                      {funnel && funnel.marks_pending != null
                        ? fmtNum(funnel.marks_pending)
                        : "—"}
                    </strong>
                    {funnel && funnel.pending_marks_upload != null ? (
                      <small className="d-block text-muted mt-1">
                        Awaiting upload: {fmtNum(funnel.pending_marks_upload)}
                      </small>
                    ) : null}
                  </Col>
                </Row>

                {ctRows.length > 0 ? (
                  <>
                    <h6 className="text-uppercase text-muted mt-3 mb-2">
                      By content type
                    </h6>
                    <Row>
                      {ctRows.slice(0, 12).map((row, i) => (
                        <Col xs="6" md="3" key={i} className="mb-2">
                          <small className="text-muted d-block text-truncate">
                            {row.directory ||
                              row.content_type ||
                              row.type ||
                              "—"}
                          </small>
                          <span className="font-weight-bold">
                            {fmtNum(
                              row.count != null ? row.count : row.uploads
                            )}
                          </span>
                        </Col>
                      ))}
                    </Row>
                  </>
                ) : null}
              </>
            )}
          </CardBody>
        </Card>
      </Col>
    </Row>
  );
}
